// TOUR-2 — static integrity helpers: tour steps ⊆ registry (browser-safe).

import type { FireflyTourArtifact } from "./tourTypes";
import { FIREFLY_TOUR_CATALOG } from "./tourCatalog";
import { FIREFLY_TOUR_PILOT_CAPTURE } from "./pilotTourArtifacts";
import { FIREFLY_ASK_PILOT_ARTIFACTS } from "./pilotAskArtifacts";
import { FIREFLY_TARGET_REGISTRY } from "./fireflyTargetRegistry";

/** DevPanel-only demo buttons — not production tour surfaces. */
export const FIREFLY_DEV_TARGET_IDS = new Set(["dev-highlight-demo-a", "dev-highlight-demo-b"]);

/** Shell files that wire `mode-nav-{modeId}` via `fireflyModeNavTargetId()`. */
export const FIREFLY_MODE_NAV_WIRING_SOURCES = [
  "src/components/shell/Sidebar/SidebarModeList.tsx",
  "src/components/shell/MobileBottomTabBar.tsx",
] as const;

/** Shell file that wires bucket-switcher + `bucket-nav-{bucketId}`. */
export const FIREFLY_BUCKET_NAV_WIRING_SOURCES = [
  "src/components/shell/Sidebar/BucketSwitcher.tsx",
] as const;

/**
 * Production targets rendered only under runtime conditions. Literal id must
 * still appear in the cited source file; presence in DOM is not guaranteed.
 */
export const FIREFLY_CONDITIONAL_DOM_TARGETS: Readonly<Record<string, string>> = {
  "crm-send-toast": "src/components/shell/WorkspaceToastOverlay.tsx",
  "stale-wac-reminder": "src/components/activity/sections/ActivityDockStaleWacReminderSection.tsx",
  "ff-intake-fill": "src/components/fulfillment/FulfillmentIntakePanel.tsx",
  "ff-intake-review": "src/components/fulfillment/FulfillmentIntakePanel.tsx",
  "ap-suggest-edit": "src/components/platform/AiPromptingPanel.tsx",
  "mfg-inventory-surface": "src/components/manufacturing/ManufacturingInventoryPanel.tsx",
  "bucket-switcher": "src/components/shell/Sidebar/BucketSwitcher.tsx",
  "admin-org-profile-save": "src/components/admin/OrgSettingsSection.tsx",
  "ops-missing-paperwork": "src/components/ops/OpsPaperworkPanel.tsx",
  "ops-deals-tab": "src/components/ops/OpsPaperworkTabs.tsx",
  "ops-locations-tab": "src/components/ops/OpsPaperworkTabs.tsx",
  "ops-deals-list": "src/components/ops/OpsPaperworkDealsTab.tsx",
  "ops-deal-checklist": "src/components/ops/OpsDealPacketSlider.tsx",
  "rto-mfg-inherit": "src/components/ops/RtoSettingsSection.tsx",
  "rto-terms-table": "src/components/ops/RtoSettingsOrgTermsEditor.tsx",
  "logi-routes-surface": "src/components/logistics/LogisticsRoutesPanel.tsx",
  "logi-route-detail": "src/components/logistics/LogisticsRoutesDetailCard.tsx",
  "logi-route-stops": "src/components/logistics/LogisticsRoutesDetailCard.tsx",
  "logi-carriers-surface": "src/components/logistics/LogisticsCarriersPanel.tsx",
  "logi-carriers-form": "src/components/logistics/LogisticsCarriersCreateForm.tsx",
  "logi-carriers-list": "src/components/logistics/LogisticsCarriersList.tsx",
  "logi-carrier-active-toggle": "src/components/logistics/LogisticsCarriersDisplayRow.tsx",
  "platform-context": "src/components/activity/sections/ActivityDockPlatformContextSection.tsx",
  "dev-simulator": "src/components/shell/popovers/ImpersonationPopover.tsx",
  "dev-impersonation-apply": "src/components/common/DevImpersonationSettingsSection.tsx",
  "firefly-catalog-chip": "src/components/activity/ActivityDockAskFirefly.tsx",
  "firefly-catalog": "src/components/activity/ActivityDockFireflyCatalog.tsx",
  "sidebar-firefly-dock": "src/components/shell/Sidebar/SidebarFireflyDockItem.tsx",
};

export function isFireflyDevTarget(targetId: string): boolean {
  return FIREFLY_DEV_TARGET_IDS.has(targetId.trim());
}

export function isFireflyModeNavTarget(targetId: string): boolean {
  return targetId.startsWith("mode-nav-");
}

export { isFireflyBucketNavTarget } from "./fireflyBucketNavTargets";

export function modeIdFromNavTarget(targetId: string): string {
  return targetId.slice("mode-nav-".length);
}

export function collectRegistryTargetIds(): string[] {
  return FIREFLY_TARGET_REGISTRY.map((t) => t.id);
}

export function collectProductionRegistryTargets() {
  return FIREFLY_TARGET_REGISTRY.filter((t) => !isFireflyDevTarget(t.id));
}

export function collectTourAnchorIds(artifacts: readonly FireflyTourArtifact[]): string[] {
  const ids = new Set<string>();
  for (const artifact of artifacts) {
    for (const step of artifact.steps) {
      const anchorId = step.target.anchorId?.trim();
      if (anchorId) ids.add(anchorId);
    }
  }
  return [...ids].sort();
}

export function collectAllShippedTourAnchorIds(): string[] {
  return collectTourAnchorIds([...FIREFLY_TOUR_CATALOG, FIREFLY_TOUR_PILOT_CAPTURE]);
}

export function collectAskHighlightTargetIds(): string[] {
  const ids = new Set<string>();
  for (const ask of FIREFLY_ASK_PILOT_ARTIFACTS) {
    const t = ask.highlightTarget?.trim();
    if (t) ids.add(t);
  }
  return [...ids].sort();
}

export type FireflyTargetIntegrityReport = {
  tourAnchorsMissingFromRegistry: string[];
  askTargetsMissingFromRegistry: string[];
  registryDuplicates: string[];
  productionTargetsMissingDom: string[];
  modeNavErrors: string[];
};
